import { Check, Copy, PencilLine } from 'lucide-react'
import { FC, PropsWithChildren, ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

// Shared by the errand-page action cards and the inline chat action cards, so the card chrome
// and draft icon/label map live in one place.

// Icon + i18n label per draft type, keyed by the base type the `createDraft` tool emits. Persisted
// errand actions use an `-draft` suffix (text-draft), so normalize with `draftBaseType`.
export const DRAFT_META = {
  text: { icon: <PencilLine size={16} />, labelKey: 'aiChat.action.draft.text' },
} as const

export type DraftBaseType = keyof typeof DRAFT_META

/**
 * The fallback is what keeps this map from having to know about withdrawn types. Email drafts
 * are no longer produced, but rows written before that are still stored and still returned, and
 * they render as text drafts rather than crashing on a missing icon.
 */
export const draftBaseType = (type: string): DraftBaseType => {
  const base = type.replace('-draft', '')

  return base in DRAFT_META ? (base as DraftBaseType) : 'text'
}

export const CardShell: FC<
  PropsWithChildren<{ className?: string; footer?: ReactNode; icon: ReactNode; label: string }>
> = ({ children, className, footer, icon, label }) => (
  <div className={cn('rounded-xl border bg-layer-1 p-4', className)}>
    <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-text-secondary">
      {icon}
      <span>{label}</span>
    </div>
    {children}
    {footer && <div className="mt-4 flex flex-wrap gap-2">{footer}</div>}
  </div>
)

/**
 * Copy-to-clipboard button for a draft's content, with a 2s "Kopierat" confirmation. Used inline
 * in the chat's draft card — the only place a draft can be copied from now that the standalone
 * errand action panel (with its Copy/Dismiss buttons) has been removed.
 */
export const CopyButton: FC<{ content: string }> = ({ content }) => {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  const onCopy = async () => {
    await navigator.clipboard.writeText(content)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Button onClick={onCopy} size="sm" variant="outline">
      {copied ? <Check /> : <Copy />}
      {copied ? t('errandsPage.actions.copied') : t('errandsPage.actions.copy')}
    </Button>
  )
}
